import React, { ReactNode, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import firebase from "firebase";
import useFirebaseProject, { FirebaseProjectConfig } from "../../../helpers/hooks/useFirebaseProject";
import { fetchAppFirebaseConfig } from "../../../helpers/firebase/appFirebase";

interface ActivityPageProps {
	title: string;
	/** The white-label app this activity belongs to, from the route params. */
	appKey?: string;
	/** Rendered once the app's Firebase project has been resolved. */
	children: (firebaseApp: firebase.app.App) => ReactNode;
}

const Wrapper = styled.div`
	min-height: 100vh;
	background-color: #f5f5f5;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 16px;
	text-align: center;
`;

const Card = styled.div`
	width: 100%;
	max-width: 440px;
	background: #ffffff;
	border-radius: 4px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
	padding: 48px 40px;
`;

export const Heading = styled.h3`
	margin-bottom: 16px;
`;

export const Copy = styled.p`
	margin-bottom: 32px;
`;

export const Muted = styled.p`
	margin: 16px 0 0;
	color: #6c757d;
`;

export const PrimaryButton = styled.button`
	background-color: #004bed;
	border: 1px solid #004bed;
	border-radius: 4px;
	color: #ffffff;
	padding: 12px 28px;
	font-weight: 600;

	&:hover,
	&:focus,
	&:active {
		background-color: #003bc2;
		border-color: #003bc2;
		color: #ffffff;
	}

	&:disabled {
		background-color: #004bed;
		border-color: #004bed;
		opacity: 0.7;
	}
`;

/**
 * Unbranded frame for the public /activity pages. These are promised to
 * customers as fully white-labeled, so nothing here may reference Event App
 * or Brushfire.
 *
 * Resolves the app's Firebase project from the public web-config endpoint and
 * hands it to `children`, showing loading / error states until then.
 */
const ActivityPage: React.FC<ActivityPageProps> = ({ title, appKey, children }) => {
	const [appConfig, setAppConfig] = useState<FirebaseProjectConfig>();
	const [error, setError] = useState<string>();
	const firebaseApp = useFirebaseProject(appConfig, appKey);

	useEffect(() => {
		let cancelled = false;
		setAppConfig(undefined);
		setError(undefined);

		if (!appKey) {
			setError("This link is missing the app it belongs to.");
			return;
		}

		fetchAppFirebaseConfig(appKey)
			.then(webConfig => {
				if (!cancelled) setAppConfig({ webConfig });
			})
			.catch(() => {
				if (!cancelled) setError("We couldn't find that app. Check the link and try again.");
			});

		return () => {
			cancelled = true;
		};
	}, [appKey]);

	const renderBody = () => {
		if (error) {
			return (
				<>
					<Heading>Something went wrong</Heading>
					<p style={{ margin: 0 }}>{error}</p>
				</>
			);
		}
		if (!firebaseApp) {
			return <Muted>Loading…</Muted>;
		}
		return children(firebaseApp);
	};

	return (
		<>
			<Helmet>
				<title>{title}</title>
			</Helmet>
			<Wrapper>
				<Card>{renderBody()}</Card>
			</Wrapper>
		</>
	);
};

export default ActivityPage;
